import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CoachingNamedItem } from '@euler/model/coaching';
import { memo, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

/** A case's tags as small muted labels; tappable with `onPress`. */
export const CaseTags = memo(
  ({
    tags,
    max,
    onPress,
  }: {
    tags: CoachingNamedItem[];
    max?: number;
    onPress?: (tag: CoachingNamedItem) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useMemo(
      () =>
        StyleSheet.create({
          tag: {
            borderRadius: rpx(6),
            paddingHorizontal: rpx(8),
            paddingVertical: rpx(2),
            backgroundColor: aiColors.muted,
          },
          pressed: { opacity: 0.6 },
        }),
      [rpx],
    );
    return (
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: rpx(6),
          marginTop: rpx(12),
        }}
      >
        {tags.slice(0, max ?? tags.length).map(tag => {
          const label = (
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              {tag.name}
            </Typography.Text>
          );
          // A plain label inside a pressable card, so the card takes the tap.
          return onPress ? (
            <Pressable
              key={tag.slug}
              onPress={() => onPress(tag)}
              accessibilityRole="link"
              style={({ pressed }) => [styles.tag, pressed && styles.pressed]}
            >
              {label}
            </Pressable>
          ) : (
            <View key={tag.slug} style={styles.tag}>
              {label}
            </View>
          );
        })}
      </View>
    );
  },
);
